import React, { useEffect, useState } from "react";
import api from "../../api/api";
import { CURRENCY_CODE } from "../../config/constants";


const ApplyForMembership = () => {
    const [step, setStep] = useState(1); // Multi-step control
    const [memberships, setMemberships] = useState([]); // Membership data
    const [selectedMembership, setSelectedMembership] = useState(null); // Selected membership
    const [loading, setLoading] = useState(true);

    // Fetch memberships on mount
    useEffect(() => {
        const fetchMemberships = async () => {
            try {
                const response = await api.get("/memberships?getAllAtOnce=true");
                setMemberships(response.data);
                setLoading(false);
            } catch (error) {
                // Error fetching memberships
                setLoading(false);
            }
        };

        fetchMemberships();
    }, []);

    // Handle membership selection
    const handleSelectMembership = (membership) => {
        setSelectedMembership(membership);
        setStep(2); // Move to the payment step
    };

    return (
        <div className="flex flex-col items-center justify-center px-2 sm:px-4 py-4 sm:py-8">
            {loading ? (
                <p className="text-dark text-lg font-bold">Loading memberships...</p>
            ) : (
                <>
                    {step === 1 && (
                        <Step1 memberships={memberships} onSelectMembership={handleSelectMembership} />
                    )}
                    {step === 2 && (
                        <Step2 membership={selectedMembership} onNext={() => setStep(3)} onBack={() => setStep(1)} />
                    )}
                    {step === 3 && <Step3 membership={selectedMembership} />}
                </>
            )}
        </div>
    );
};

export default ApplyForMembership;

/** STEP 1: Membership Selection **/
export const Step1 = ({ memberships, onSelectMembership }) => {
    return (
        <div className="w-full space-y-6 sm:space-y-12">
            <div className="grid gap-4 sm:gap-12 grid-cols-1 lg:grid-cols-2">
                {memberships.map((membership) => (
                    <div
                        key={membership.id}
                        className="bg-white rounded-2xl sm:rounded-3xl shadow-lg sm:shadow-xl p-4 sm:p-8 
                            flex flex-col justify-between transition-transform transform hover:scale-105 relative"
                    >
                        {/* Ribbon for Best Value */}
                        {membership.name === "Dyo Membership" && (
                            <span className="absolute -top-2 right-2 sm:top-0 sm:right-0 
                                bg-accent text-white text-xs font-bold py-1 px-2 sm:px-3 
                                rounded-bl-2xl sm:rounded-bl-3xl shadow-lg">
                                Best Value
                            </span>
                        )}

                        {/* Membership Details */}
                        <div className="space-y-4 sm:space-y-6">
                            <div>
                                <h3 className="text-xl sm:text-2xl font-bold text-accent mb-2">
                                    {membership.name}
                                </h3>
                                <p className="text-base sm:text-lg font-semibold text-dark">
                                    {CURRENCY_CODE} {membership.price}
                                    <span className="text-xs sm:text-sm font-medium">/ month</span>
                                </p>
                            </div>
                            <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-line">
                                {membership.description}
                            </p>

                            {/* Membership Benefits */}
                            {membership.benefits && membership.benefits.length > 0 && (
                                <div className="mt-4">
                                    <h4 className="text-base sm:text-lg font-semibold text-gray-800 mb-2">
                                        Benefits:
                                    </h4>
                                    <div className="grid grid-cols-1 gap-2 sm:gap-4">
                                        {membership.benefits.map((benefit) => (
                                            <div
                                                key={benefit.id}
                                                className="flex items-center space-x-2 sm:space-x-4 p-2 sm:p-3 
                                                    border rounded-lg shadow-sm hover:shadow-md transition"
                                            >
                                                {/* Benefit Icon */}
                                                {benefit.icon && (
                                                    <img
                                                        src={benefit.icon.url}
                                                        alt={benefit.name}
                                                        className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
                                                    />
                                                )}
                                                {/* Benefit Details */}
                                                <div>
                                                    <p className="text-sm sm:text-base font-semibold text-gray-800">
                                                        {benefit.name}
                                                    </p>
                                                    {benefit.description && (
                                                        <p className="text-xs text-gray-600">
                                                            {benefit.description}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action Button */}
                        <button
                            className="mt-4 sm:mt-6 w-full py-2 sm:py-3 bg-primary text-white 
                                text-sm sm:text-base font-bold rounded-lg shadow-md 
                                hover:bg-dark transition-colors"
                            onClick={() => onSelectMembership(membership)}
                        >
                            Treat Yourself with {membership.name}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};

/** STEP 2: Payment Confirmation **/
export const Step2 = ({ membership, onBack, onNext }) => {
    return (
        <div className="max-w-2xl w-full mx-auto bg-white rounded-2xl sm:rounded-3xl 
            shadow-lg sm:shadow-xl p-4 sm:p-10">
            <h2 className="text-xl sm:text-4xl font-extrabold text-primary mb-4 sm:mb-6 text-center">
                Proceed to Payment
            </h2>

            <div className="space-y-3 sm:space-y-4">
                <p className="text-gray-700 text-sm sm:text-lg text-center">
                    You are about to proceed with the <strong>{membership.name}</strong> membership.
                </p>
                <p className="text-gray-600 text-center text-xs sm:text-base">
                    Membership Price: <span className="font-semibold">
                        AED {membership.price}</span> / month
                </p>

                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 
                    p-3 sm:p-4 rounded text-xs sm:text-base">
                    <p className="font-medium">
                        <strong>Important:</strong> Ensure your details match your account information.
                    </p>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-center 
                mt-6 sm:mt-10 space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                    type="button"
                    onClick={onBack}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-gray-200 
                        text-gray-800 text-sm sm:text-base font-medium rounded-lg 
                        shadow hover:bg-gray-300 transition"
                >
                    Back
                </button>
                <button
                    type="button"
                    onClick={() => {
                        window.location.href = membership.payment_url;
                    }}
                    className="w-full sm:w-auto px-4 sm:px-6 py-2 sm:py-3 bg-primary 
                        text-white text-sm sm:text-base font-bold rounded-lg shadow 
                        hover:bg-primary-dark transition"
                >
                    Proceed to Payment
                </button>
            </div>
        </div>
    );
};

/** STEP 3: Thank You / Confirmation **/
export const Step3 = ({ membership }) => {
    return (
        <div className="w-full max-w-lg bg-white rounded-2xl sm:rounded-3xl shadow-lg 
            p-4 sm:p-8 text-center mx-auto">
            <h2 className="text-xl sm:text-3xl font-bold text-primary mb-3 sm:mb-4">
                Thank You!
            </h2>

            <p className="text-body text-xs sm:text-base mb-4 sm:mb-6">
                You are now a proud member of the <strong>{membership.name}</strong> plan!
                Enjoy all the exclusive benefits and perks we have to offer.
            </p>

            <button
                className="w-full sm:w-auto px-4 sm:px-6 py-2 bg-primary text-white 
                    text-sm sm:text-base rounded-lg shadow hover:bg-accent 
                    transition-transform transform hover:scale-105"
                onClick={() => alert("Redirect to dashboard")}
            >
                Go to Dashboard
            </button>
        </div>
    );
};

